import { LoadingSpinner } from "components/LoadingSpinner";
import { InfoIcon } from "components/PrecalculatedAllowablesModal/InfoIcon";
import { PrecalculatedAllowablesModal } from "components/PrecalculatedAllowablesModal/PrecalculatedAllowablesModal";
import { parsePrice } from "helpers/price";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tooltip } from "primereact/tooltip";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { userSelector } from "store/slices/user";
import styled from "styled-components";
import { IPayer } from "types/Payers/Payers";
import { ICaseRateRow } from "types/ProviderPayout";

const StyledContainer = styled.div`
  border-radius: 8px;
  border: 1px solid var(--gray-new-200);
  display: flex;
  flex-direction: column;
  width: 100%;
`;

// Extract payer columns dynamically (excluding implant cost columns)
function extractPayerColumns(data: ICaseRateRow[]): string[] {
  if (!data.length) return [];

  const firstRow = data[0];
  const staticColumns = [
    "case_signature",
    "case_signature_with_amounts",
    "case_description",
    "primary_cpt_code",
    "total_orders",
    "Average",
  ];

  return Object.keys(firstRow).filter(
    (key) => !staticColumns.includes(key) && !key.includes("_implant")
  );
}

// Helper function to get implant cost for a payer
function getImplantCost(
  rowData: ICaseRateRow,
  payerName: string
): number | null {
  const implantKey = `${payerName}_implant`;
  const value = rowData[implantKey];
  if (value === null || value === undefined) return null;
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  return typeof numValue === "number" && !Number.isNaN(numValue)
    ? numValue
    : null;
}

// Helper function to truncate text
function truncateText(text: string, maxLength = 16): string {
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

// Helper function to format price without decimals
function formatPriceNoCents(value: number): string {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

// Helper function to render payer column with implant cost
function renderPayerColumn(rowData: ICaseRateRow, payerName: string) {
  const value = rowData[payerName];
  const implantCost = getImplantCost(rowData, payerName);
  const hasImplantCost = implantCost && implantCost > 0;

  if (value === null || value === undefined) {
    return "-";
  }

  // Handle both numbers and strings for main price
  const numValue = typeof value === "string" ? parseFloat(value) : value;

  if (typeof numValue !== "number" || Number.isNaN(numValue)) {
    return value;
  }

  const formattedPrice = formatPriceNoCents(Math.round(numValue));

  if (!hasImplantCost) {
    return formattedPrice;
  }

  const totalWithImplant = Math.round(numValue + implantCost);
  const caseSignature = rowData.case_signature?.replace(/[^a-zA-Z0-9]/g, "_");
  const cleanPayerName = payerName.replace(/[^a-zA-Z0-9]/g, "_");
  const tooltipClass = `implant-tooltip-${caseSignature}-${cleanPayerName}`;

  return (
    <div className="flex flex-column">
      <div>{formattedPrice}</div>
      <Tooltip target={`.${tooltipClass}`} />
      <div
        className={`${tooltipClass} cursor-pointer`}
        data-pr-tooltip={`Implant Payment is ${parsePrice(implantCost)}`}
        style={{
          fontSize: "0.75rem",
          fontStyle: "italic",
          color: "#6b7280",
          marginTop: "2px",
        }}
      >
        Total: {formatPriceNoCents(totalWithImplant)}
      </div>
    </div>
  );
}

// Description render function
const renderDescription = (rowData: any) => {
  const description = rowData.case_description;
  const truncated = truncateText(description);
  const needsTooltip = description && description.length > 16;

  if (!needsTooltip) {
    return description;
  }

  const cleanSignature = rowData.case_signature?.replace(/[^a-zA-Z0-9]/g, "_");

  return (
    <>
      <Tooltip target={`.description-${cleanSignature}`} position="top" />
      <span
        className={`cursor-pointer description-${cleanSignature}`}
        data-pr-tooltip={description}
      >
        {truncated}
      </span>
    </>
  );
};

// Case signature render function
const renderCaseSignature = (rowData: any) => {
  const signature = rowData.case_signature;
  const truncated = truncateText(signature);
  const needsTooltip = signature && signature.length > 16;

  if (!needsTooltip) {
    return signature;
  }

  const cleanSignature = signature?.replace(/[^a-zA-Z0-9]/g, "_");

  return (
    <>
      <Tooltip target={`.signature-${cleanSignature}`} position="top" />
      <span
        className={`cursor-pointer signature-${cleanSignature}`}
        data-pr-tooltip={signature}
      >
        {truncated}
      </span>
    </>
  );
};

export function TopCasesWithPayers({
  dataFromAPI,
  isLoading,
  isFetching,
  payers = [],
}: {
  dataFromAPI: any;
  isLoading: boolean;
  isFetching: boolean;
  payers?: IPayer[];
}) {
  // Get accountId from Redux store
  const userState = useSelector(userSelector);
  const accountId = userState.currentAccount;

  // Modal state for precalculated allowables
  const [modalVisible, setModalVisible] = useState(false);
  const [modalParams, setModalParams] = useState<{
    accountId: string;
    payerId: number;
    caseSignature: string;
  } | null>(null);

  // Extract dynamic payer columns from the data
  const payerColumns = useMemo(() => {
    return extractPayerColumns(dataFromAPI?.data || []);
  }, [dataFromAPI?.data]);

  // Create payer name to ID mapping (both original and sanitized names)
  const payerNameToIdMap = useMemo(() => {
    const map = new Map<string, number>();
    payers.forEach((payer) => {
      // Map original name
      map.set(payer.name, payer.id);
      // Also map sanitized name (same logic as backend: replace non-alphanumeric with underscore)
      const sanitizedName = payer.name.replace(/[^a-zA-Z0-9]/g, "_");
      map.set(sanitizedName, payer.id);
    });
    return map;
  }, [payers]);

  // Handler for info icon click
  const handleInfoIconClick = (rowData: ICaseRateRow, payerName: string) => {
    const caseSignature = rowData.case_signature || "";

    // Get payerId from mapping - the map includes both sanitized and original names
    // payerName here is the sanitized column name (e.g., "BCBS_Kansas" or "Blue_Select___Plus")
    const payerId = payerNameToIdMap.get(payerName);

    // Validate we have all required data
    if (!accountId) {
      console.error("Cannot open modal: accountId is not available");
      return;
    }

    if (!payerId) {
      console.error(
        `Cannot open modal: payerId not found for payer "${payerName}"`
      );
      return;
    }

    if (!caseSignature) {
      console.error("Cannot open modal: caseSignature is missing");
      return;
    }

    // Open modal with parameters
    setModalParams({
      accountId,
      payerId,
      caseSignature,
    });
    setModalVisible(true);
  };

  // Render payer column with InfoIcon
  const renderPayerColumnWithIcon = (
    rowData: ICaseRateRow,
    payerName: string
  ) => {
    const priceDisplay = renderPayerColumn(rowData, payerName);

    // Only show info icon if there's a valid price
    const value = rowData[payerName];
    const hasValidPrice =
      value !== null && value !== undefined && value !== "-";

    if (!hasValidPrice) {
      return priceDisplay;
    }

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ flex: 1 }}>{priceDisplay}</div>
        <InfoIcon onClick={() => handleInfoIconClick(rowData, payerName)} />
      </div>
    );
  };

  // Handler for closing modal
  const handleCloseModal = () => {
    setModalVisible(false);
    setModalParams(null);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="flex flex-column gap-3">
      <StyledContainer className="bg-white">
        <div className="p-4">
          <p className="text-md font-bold mb-2">Top 10 Cases with Payers</p>
          <DataTable
            rowHover
            stripedRows
            dataKey="case_signature"
            editMode="cell"
            className="p-fluid datatable__TopCasesWithPayers"
            value={dataFromAPI?.data || []}
            loading={isFetching}
            responsiveLayout="scroll"
            emptyMessage="No case rates to show."
          >
            <Column
              field="case_description"
              header="Description"
              style={{ minWidth: "200px" }}
              body={renderDescription}
            />
            <Column
              field="case_signature"
              header="Case Signature"
              style={{ minWidth: "120px" }}
              body={renderCaseSignature}
            />
            <Column
              field="primary_cpt_code"
              header="Primary Code"
              style={{ minWidth: "100px" }}
            />
            <Column
              field="total_orders"
              header="Total Orders"
              style={{ minWidth: "110px" }}
            />

            {/* Dynamic payer columns */}
            {payerColumns.map((payerName) => (
              <Column
                key={payerName}
                field={payerName}
                header={payerName.replace(/_/g, " ")}
                style={{ minWidth: "140px" }}
                body={(rowData) =>
                  renderPayerColumnWithIcon(rowData, payerName)
                }
              />
            ))}

            <Column
              field="Average"
              header="Average"
              style={{ minWidth: "100px" }}
              body={(rowData) => {
                const value = rowData.Average;
                if (value === null || value === undefined) {
                  return "-";
                }
                return formatPriceNoCents(Math.round(value));
              }}
            />
          </DataTable>
        </div>
      </StyledContainer>

      {/* Precalculated Allowables Modal */}
      {modalParams && (
        <PrecalculatedAllowablesModal
          visible={modalVisible}
          onHide={handleCloseModal}
          accountId={modalParams.accountId}
          payerId={modalParams.payerId}
          caseSignature={modalParams.caseSignature}
        />
      )}
    </div>
  );
}
